import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Icon, Chip, Checkbox, IconButton,
} from '@mui/material';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import Modal from 'global/components/modal';
import { FIELD_CATEGORIES, getCategoryByComponent, getDefaultFieldConfig } from './constants';
import TaskTypeFieldConfig from './TaskTypeFieldConfig';
import { get, size } from 'global/lib/lodash';
import useStyles from './styles';

const isDefault = (field) => field.id.startsWith('f_');

const TEXT_SUB_TYPE_LABELS = { TextField: 'TEXT', LongText: 'LONG TEXT', RichText: 'RICH TEXT' };

const getTypeLabel = (field) => {
  if (field.type_label) return field.type_label;
  const catEntry = getCategoryByComponent(field.field_type);
  if (catEntry && catEntry[0] === 'Text') {
    const subType = get(field, 'config.sub_type', field.field_type);
    return TEXT_SUB_TYPE_LABELS[subType] || catEntry[1].label.toUpperCase();
  }
  return catEntry ? catEntry[1].label.toUpperCase() : (field.field_type || 'UNKNOWN').toUpperCase();
};

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// --- Column definitions ---
// Each column: { field, title, align, width, render(field, index, actions) }
// `actions` provides: { toggleEnabled, toggleRequired, openConfig, removeField, classes }
const COLUMNS = [
  {
    field: 'label',
    title: 'Field Name',
    render: (field) => `${field.label || '(untitled)'}${isDefault(field) ? ' *' : ''}`,
  },
  {
    field: 'is_enabled',
    title: 'Enabled',
    align: 'center',
    render: (field, index, { toggleEnabled }) => (
      <Checkbox
        checked={field.is_enabled}
        disabled={field.locked}
        onChange={() => toggleEnabled(index)}
        size="small"
        color="primary"
      />
    ),
  },
  {
    field: 'is_required',
    title: 'Required**',
    align: 'center',
    render: (field, index, { toggleRequired }) => (
      <Checkbox
        checked={field.is_required}
        disabled={field.locked || !field.is_enabled}
        onChange={() => toggleRequired(index)}
        size="small"
        color="primary"
      />
    ),
  },
  {
    field: 'field_type',
    title: 'Field Type',
    align: 'center',
    render: (field, index, { classes }) => (
      <span className={field.is_enabled ? classes.typeLabel : classes.typeLabelDisabled}>
        {getTypeLabel(field)}
      </span>
    ),
  },
  {
    field: 'actions',
    title: '',
    width: 80,
    render: (field, index, { openConfig, confirmRemoveField, classes }) => {
      const def = isDefault(field);
      const disabled = def || !field.is_enabled;
      return (
        <div className={classes.actionIcons}>
          <IconButton size="small" disabled={disabled} onClick={() => openConfig(field, index)}>
            <Icon fontSize="small">settings</Icon>
          </IconButton>
          <IconButton size="small" disabled={disabled} onClick={() => confirmRemoveField(field, index)}>
            <Icon fontSize="small">delete</Icon>
          </IconButton>
        </div>
      );
    },
  },
];

const TaskTypeFieldBuilder = ({ fields, onChange, canEdit }) => {
  const classes = useStyles({});
  const [configModal, setConfigModal] = useState({ open: false, field: null, index: -1 });
  const [addModal, setAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: -1, name: '' });

  const addField = (categoryKey) => {
    const cat = FIELD_CATEGORIES[categoryKey];
    const newField = {
      id: `cf_${Date.now()}`,
      field_type: cat.component,
      label: '',
      is_required: false,
      is_enabled: true,
      order: size(fields),
      config: getDefaultFieldConfig(categoryKey),
    };
    setAddModal(false);
    setConfigModal({ open: true, field: newField, index: -1 });
  };

  const updateField = (field, index) => {
    if (index === -1) {
      const processed = processFieldOptions(field);
      onChange([...fields, { ...processed, order: size(fields) }]);
    } else {
      const processed = processFieldOptions(field);
      const updated = [...fields];
      updated[index] = processed;
      onChange(updated);
    }
    setConfigModal({ open: false, field: null, index: -1 });
  };

  const removeField = (index) => {
    const updated = [...fields];
    updated.splice(index, 1);
    onChange(updated.map((f, i) => ({ ...f, order: i })));
  };

  const toggleEnabled = (index) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], is_enabled: !updated[index].is_enabled };
    onChange(updated);
  };

  const toggleRequired = (index) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], is_required: !updated[index].is_required };
    onChange(updated);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = reorder(fields, result.source.index, result.destination.index);
    onChange(reordered.map((f, i) => ({ ...f, order: i })));
  };

  const openConfig = (field, index) => setConfigModal({ open: true, field, index });
  const confirmRemoveField = (field, index) => setDeleteConfirm({ open: true, index, name: field.label || '(untitled)' });
  const colActions = { toggleEnabled, toggleRequired, openConfig, confirmRemoveField, classes };

  return (
    <div className={classes.section}>
      <Text className={classes.sectionTitle}>Fields</Text>

      <DragDropContext onDragEnd={onDragEnd}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.fieldsHeaderCell} style={{ width: 40 }} />
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    className={classes.fieldsHeaderCell}
                    align={col.align}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <Droppable droppableId="field-builder">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {fields.map((field, index) => (
                    <Draggable
                      key={field.id}
                      draggableId={field.id}
                      index={index}
                      isDragDisabled={!canEdit}
                    >
                      {(dragProvided, snapshot) => (
                        <TableRow
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={classes.fieldsRow}
                          style={{
                            ...dragProvided.draggableProps.style,
                            ...(snapshot.isDragging ? { background: 'rgb(235,235,235)' } : {}),
                          }}
                        >
                          <TableCell style={{ width: 40 }}>
                            <span
                              {...(field.is_enabled ? dragProvided.dragHandleProps : {})}
                              className={classes.dragHandle}
                              style={!field.is_enabled ? { opacity: 0.38, pointerEvents: 'none' } : undefined}
                            >
                              <Icon fontSize="small">drag_indicator</Icon>
                            </span>
                          </TableCell>
                          {COLUMNS.map((col) => (
                            <TableCell key={col.field} align={col.align} style={col.width ? { width: col.width } : undefined}>
                              {col.render(field, index, colActions)}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </DragDropContext>

      {canEdit && (
        <Button
          className={classes.addFieldBtn}
          variant="contained"
          color="primary"
          startIcon={<Icon>add</Icon>}
          onClick={() => setAddModal(true)}
        >
          ADD NEW FIELD
        </Button>
      )}

      <Text className={classes.requiredNote}>
        * Default fields can't be modified, only disabled and/or required
      </Text>
      <Text className={classes.requiredNote}>
        ** Required fields are those required to create or save a task
      </Text>

      {/* Field Type Picker */}
      <Modal
        open={addModal}
        onClose={() => setAddModal(false)}
        title="Add Custom Field"
        minWidth={350}
      >
        <div className={classes.fieldPickerGrid}>
          {Object.entries(FIELD_CATEGORIES).map(([key, cat]) => (
            <Chip
              key={key}
              icon={<Icon>{cat.icon}</Icon>}
              label={cat.label}
              onClick={() => addField(key)}
              clickable
              variant="outlined"
            />
          ))}
        </div>
      </Modal>

      {/* Field Config Modal */}
      {configModal.open && (
        <TaskTypeFieldConfig
          open={configModal.open}
          field={configModal.field}
          onSave={(f) => updateField(f, configModal.index)}
          onClose={() => setConfigModal({ open: false, field: null, index: -1 })}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, index: -1, name: '' })}
        title="Delete Field"
        buttons={[
          { title: 'Cancel', onClick: () => setDeleteConfirm({ open: false, index: -1, name: '' }), variant: 'outlined' },
          {
            title: 'Delete',
            onClick: () => {
              removeField(deleteConfirm.index);
              setDeleteConfirm({ open: false, index: -1, name: '' });
            },
            variant: 'contained',
          },
        ]}
      >
        {`Are you sure you want to delete the field "${deleteConfirm.name}"?`}
      </Modal>
    </div>
  );
};

// Convert ChipAdd string arrays back to proper option objects
function processFieldOptions(field) {
  const processed = { ...field, config: { ...field.config } };

  if (field.field_type === 'ToggleButton' && Array.isArray(get(field, 'config.options'))) {
    const opts = field.config.options;
    if (opts.length > 0 && typeof opts[0] === 'string') {
      processed.config.options = opts.map((o) => ({ title: o, value: o.toLowerCase().replace(/\s+/g, '_') }));
    }
  }

  if (field.field_type === 'Select' && Array.isArray(get(field, 'config.options'))) {
    const opts = field.config.options;
    if (opts.length > 0 && typeof opts[0] === 'string') {
      processed.config.options = opts.map((o) => ({ label: o, value: o.toLowerCase().replace(/\s+/g, '_') }));
    }
  }

  return processed;
}

export default TaskTypeFieldBuilder;
